import React from "react";

import { withStyles, createStyles, WithStyles, Theme } from "@material-ui/core/styles";

import Paper from "@material-ui/core/Paper";
import Button from "@material-ui/core/Button";
import IconButton from "@material-ui/core/IconButton";
import RefreshIcon from "@material-ui/icons/Refresh";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import WarningIcon from "@material-ui/icons/Warning";
import ErrorIcon from "@material-ui/icons/Error";
import CircularProgress from "@material-ui/core/CircularProgress";
import Collapse from "@material-ui/core/Collapse";
import CloseIcon from "@material-ui/icons/Close";
import ExpandLessIcon from "@material-ui/icons/ExpandLess";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import LocationOnIcon from "@material-ui/icons/LocationOn";
import LayersIcon from "@material-ui/icons/Layers";
import { Row } from "react-data-grid";
import format from "date-fns/format";

import "react-splitter-layout/lib/index";
import "../lib/custom/splitter.css";
import Splitter from "m-react-splitters";
import "m-react-splitters/lib/splitters.css";

import Grid from "../lib/components/grid";
import { GridColumns as SummaryColumns } from "./stockchecksummarygridsetup";
import { GridColumnsSystem, GridColumnsFile } from "./stockcheckcomparisongridsetup";
import { getStockCheckFileData, getStockCheckSummary, getStockCheckSystemData, StockCheckRow, StockCheckSummary } from "../lib/api/stockcheck";
import { SnackContext } from "../lib/context/SnackContext";
import { GenerateErrorMessage } from "../lib/helpers/string_methods";

const styles = (theme: Theme) =>
  createStyles({
    root: {
      padding: theme.spacing(2),
      width: "100%",
      height: "calc(100vh - 100px)",
      display: "flex",
      flexDirection: "column",
      gap: theme.spacing(2),
      overflow: "auto",
      "& > *": { flexShrink: 0 },
    },
    header: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
    },
    title: {
      margin: 0,
      color: theme.palette.primary.main,
      fontWeight: 600,
    },
    cardsContainer: {
      display: "flex",
      gap: theme.spacing(2),
      flexWrap: "wrap",
    },
    card: {
      position: "relative",
      minWidth: 200,
      flex: "1 1 200px",
      display: "flex",
      alignItems: "center",
      gap: theme.spacing(2),
      padding: theme.spacing(2.5, 2),
      borderRadius: 12,
      backgroundColor: theme.palette.background.paper,
      boxShadow: "0 1px 6px rgba(0, 0, 0, 0.08)",
      transition: "box-shadow 0.2s",
      "&:hover": {
        boxShadow: "0 4px 14px rgba(0, 0, 0, 0.12)",
      },
    },
    iconBubble: {
      width: 56,
      height: 56,
      borderRadius: "50%",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      flexShrink: 0,
    },
    cardIcon: {
      fontSize: 26,
    },
    cardInfo: {
      display: "flex",
      flexDirection: "column",
      minWidth: 0,
    },
    cardValue: {
      fontSize: 24,
      fontWeight: 700,
      lineHeight: 1.2,
      color: theme.palette.text.primary,
    },
    cardLabel: {
      fontSize: 14,
      color: theme.palette.text.secondary,
      marginTop: 2,
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    },
    gridSection: {
      display: "flex",
      flexDirection: "column",
    },
    gridHeader: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      marginBottom: theme.spacing(1),
    },
    gridTitle: {
      margin: 0,
      fontSize: 16,
      fontWeight: 500,
    },
    detailSection: {
      padding: theme.spacing(2),
      borderRadius: 12,
      backgroundColor: theme.palette.background.paper,
      boxShadow: "0 1px 6px rgba(0, 0, 0, 0.08)",
    },
    detailHeader: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      marginBottom: theme.spacing(1.5),
    },
    detailTitleContainer: {
      display: "flex",
      alignItems: "center",
      gap: theme.spacing(1),
    },
    detailFile: {
      color: theme.palette.text.secondary,
      fontSize: 13,
    },
    locationBadge: {
      backgroundColor: theme.palette.primary.main,
      color: "white",
      padding: "4px 12px",
      borderRadius: 16,
      fontSize: 14,
      fontWeight: 500,
    },
    selectedRow: {
      fontWeight: "bold",
      backgroundColor: "#e3f2fd",
    },
    errorRow: {
      backgroundColor: "#D0342C",
      color: "white",
      "&:hover": {
        backgroundColor: "#D0342C",
      },
    },
    loadingOverlay: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      padding: theme.spacing(4),
    },
    resizeHandle: {
      height: 14,
      margin: theme.spacing(0.5, 0),
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      cursor: "row-resize",
      position: "relative",
      zIndex: 2,
      "&:hover > div, &:active > div": {
        backgroundColor: theme.palette.primary.main,
      },
    },
    resizeGrip: {
      width: 56,
      height: 4,
      borderRadius: 2,
      backgroundColor: theme.palette.divider,
      transition: "background-color 0.15s",
    },
    splitterWrapper: {
      position: "relative",
      height: "100%",
      width: "100%",
    },
    splitter: {
      gap: "10px",
    },
    comparisonPane: {
      display: "flex",
      flexDirection: "column",
      height: "100%",
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: 8,
      overflow: "hidden",
      backgroundColor: theme.palette.background.paper,
    },
    paneTitleAnchor: {
      position: "relative",
      height: 0,
      overflow: "visible",
      zIndex: 1,
    },
    comparisonHeader: {
      position: "absolute",
      top: 20,
      left: 12,
      transform: "translateY(-50%)",
      display: "flex",
      alignItems: "center",
      gap: theme.spacing(1),
      whiteSpace: "nowrap",
      pointerEvents: "none",
      fontWeight: 600,
      fontSize: 14,
      color: theme.palette.text.primary,
    },
    paneDot: {
      width: 8,
      height: 8,
      borderRadius: "50%",
      flexShrink: 0,
    },
    paneCount: {
      padding: "2px 10px",
      borderRadius: 12,
      fontSize: 12,
      fontWeight: 600,
    },
  });

type StockCheckProps = {
  match: any;
  history: any;
} & WithStyles<typeof styles>;

// Validation columns to compare between file and system
const validationCols = ["Barcode", "Farm Number", "Commodity Code", "Variety Code", "Pack Code", "Mark Code", "Grade Code", "Count Code", "Location Code", "No Cartons", "Pallet Size", "Orig Intake Waybill"];

const Palette = {
  total: { color: "#1565c0", bg: "#e3f2fd" },
  balanced: { color: "#2e7d32", bg: "#e8f5e9" },
  differences: { color: "#e65100", bg: "#fff3e0" },
  missing: { color: "#c62828", bg: "#ffebee" },
  pallets: { color: "#6a1b9a", bg: "#f3e5f5" },
};

const MIN_GRID_HEIGHT = 120;
const DEFAULT_SUMMARY_HEIGHT = 400;
const DEFAULT_COMPARISON_HEIGHT = 420;
const FULL_SUMMARY_HEIGHT = 350;

class StockCheckUnstyled extends React.Component<StockCheckProps, {}> {
  static contextType = SnackContext;

  state = {
    loading: true,
    detailLoading: false,
    summaryData: [] as StockCheckSummary[],
    systemData: [] as StockCheckRow[],
    fileData: [] as StockCheckRow[],
    selectedLocation: undefined as StockCheckSummary | undefined,
    summaryHeight: DEFAULT_SUMMARY_HEIGHT,
    comparisonHeight: DEFAULT_COMPARISON_HEIGHT,
    comparisonCollapsed: false,
  };

  private dragStartY = 0;
  private dragDelta = 0;
  private handleRef = React.createRef<HTMLDivElement>();

  componentDidMount() {
    this.loadSummary();
  }

  componentWillUnmount() {
    this.stopResizeListeners();
  }

  stopResizeListeners = () => {
    document.removeEventListener("mousemove", this.onResizeMove);
    document.removeEventListener("mouseup", this.onResizeEnd);
    document.body.style.userSelect = "";
    document.body.style.cursor = "";
  };

  startResize = (e: React.MouseEvent) => {
    e.preventDefault();
    this.dragStartY = e.clientY;
    this.dragDelta = 0;
    document.addEventListener("mousemove", this.onResizeMove);
    document.addEventListener("mouseup", this.onResizeEnd);
    document.body.style.userSelect = "none";
    document.body.style.cursor = "row-resize";
  };

  onResizeMove = (e: MouseEvent) => {
    const { summaryHeight, comparisonHeight } = this.state;
    const delta = e.clientY - this.dragStartY;
    this.dragDelta = Math.max(MIN_GRID_HEIGHT - summaryHeight, Math.min(delta, comparisonHeight - MIN_GRID_HEIGHT));
    if (this.handleRef.current) {
      this.handleRef.current.style.transform = `translateY(${this.dragDelta}px)`;
    }
  };

  onResizeEnd = () => {
    this.stopResizeListeners();
    if (this.handleRef.current) {
      this.handleRef.current.style.transform = "";
    }
    const delta = this.dragDelta;
    this.dragDelta = 0;
    if (delta !== 0) {
      this.setState(({ summaryHeight, comparisonHeight }: any) => ({ summaryHeight: summaryHeight + delta, comparisonHeight: comparisonHeight - delta }));
    }
  };

  resetResize = () => {
    this.setState({ summaryHeight: DEFAULT_SUMMARY_HEIGHT, comparisonHeight: DEFAULT_COMPARISON_HEIGHT });
  };

  toggleComparison = () => {
    this.setState(({ comparisonCollapsed }: any) => ({ comparisonCollapsed: !comparisonCollapsed }));
  };

  loadSummary = async () => {
    this.setState({ loading: true, systemData: [], fileData: [], selectedLocation: undefined });

    try {
      const result = await getStockCheckSummary();
      this.setState({
        loading: false,
        summaryData: result.data.map((row) => ({ ...row, formattedDate: format(new Date(row.timestamp), "yyyy-MM-dd HH:mm") })),
      });
    } catch (error) {
      this.setState({ loading: false });
      const err = GenerateErrorMessage(error, "Error running stock check.");
      this.context.updateSnack({ show: true, color: "red", message: err });
    }
  };

  handleComparison = (data: StockCheckRow[], dataSet: Record<string, StockCheckRow>): StockCheckRow[] => {
    return data.reduce((arr: StockCheckRow[], item) => {
      const key = `${item.Barcode}_${item["Sequence Number"]}`;
      const currentItem = dataSet[key];

      if (!currentItem) {
        arr.push({ ...item, edicomparisonerror: "Missing" });
        return arr;
      }

      const cols: string[] = [];
      validationCols.forEach((col) => {
        if (col === "No Cartons" || col === "Pallet Size") {
          if (parseFloat(String(currentItem[col])) !== parseFloat(String(item[col]))) {
            cols.push(col);
          }
        } else if ((currentItem[col] || "").toString().trim() !== (item[col] || "").toString().trim()) {
          cols.push(col);
        }
      });

      if (cols.length === 0) {
        arr.push(item);
      } else {
        // Filter out carton differences for SCHOONB location
        const filtered = cols.filter((c) => !(c === "No Cartons" && (currentItem["Location Code"] || "").toUpperCase() === "SCHOONB"));
        arr.push({ ...item, edicomparisonerror: filtered.length > 0 ? filtered.join(",") : undefined });
      }

      return arr;
    }, []);
  };

  getValidation = (systemData: StockCheckRow[], fileData: StockCheckRow[]) => {
    const fileDataKeySet = fileData.reduce((obj, item) => {
      obj[`${item.Barcode}_${item["Sequence Number"]}`] = item;
      return obj;
    }, {} as Record<string, StockCheckRow>);

    const systemDataKeySet = systemData.reduce((obj, item) => {
      obj[`${item.Barcode}_${item["Sequence Number"]}`] = item;
      return obj;
    }, {} as Record<string, StockCheckRow>);

    const comparisonFile = this.handleComparison(fileData, systemDataKeySet);
    const comparisonSystem = this.handleComparison(systemData, fileDataKeySet);

    return { comparisonSystem, comparisonFile };
  };

  loadDetail = async (row: StockCheckSummary) => {
    this.setState({ detailLoading: true, selectedLocation: row, comparisonCollapsed: false });

    try {
      const [systemResult, fileResult] = await Promise.all([getStockCheckSystemData(row.FileImport_id), getStockCheckFileData(row.FileImport_id)]);

      const { comparisonSystem, comparisonFile } = this.getValidation(systemResult.data, fileResult.data);

      this.setState({
        detailLoading: false,
        systemData: comparisonSystem,
        fileData: comparisonFile,
      });
    } catch (error) {
      this.setState({ detailLoading: false });
      const err = GenerateErrorMessage(error, "Error loading stock check detail.");
      this.context.updateSnack({ show: true, color: "red", message: err });
    }
  };

  closeDetail = () => {
    this.setState({ selectedLocation: undefined, systemData: [], fileData: [] });
  };

  getStats = () => {
    const { summaryData } = this.state;
    return {
      total: summaryData.length,
      balanced: summaryData.filter((r) => r.Result === "Balanced").length,
      missing: summaryData.filter((r) => r.Result === "Stock Missing").length,
      differences: summaryData.filter((r) => r.Result === "Detail Differences").length,
      totalPallets: summaryData.reduce((sum, r) => sum + r["Total Pallets"], 0),
      totalMatched: summaryData.reduce((sum, r) => sum + r.Matched, 0),
    };
  };

  summaryRowRenderer = (props, classes) => {
    const { row } = props;
    const selected = this.state.selectedLocation && this.state.selectedLocation["Location Code"] === row["Location Code"];

    return <Row {...props} className={selected ? classes.selectedRow : undefined} onClick={() => this.loadDetail(row)} />;
  };

  comparisonRowRenderer = (props, classes) => {
    const { row } = props;
    const hasError = (row.edicomparisonerror || "").includes("Missing");

    return <Row {...props} className={hasError ? classes.errorRow : undefined} />;
  };

  renderPaneTitle = (label: string, count: number, tone: { color: string; bg: string }) => {
    const { classes } = this.props;
    return (
      <div className={classes.paneTitleAnchor}>
        <div className={classes.comparisonHeader}>
          <span className={classes.paneDot} style={{ backgroundColor: tone.color }} />
          <span>{label}</span>
          <span className={classes.paneCount} style={{ color: tone.color, backgroundColor: tone.bg }}>
            {count.toLocaleString()} pallets
          </span>
        </div>
      </div>
    );
  };

  render() {
    const { classes } = this.props;
    const { loading, detailLoading, summaryData, systemData, fileData, selectedLocation, summaryHeight, comparisonHeight, comparisonCollapsed } = this.state;
    const stats = this.getStats();

    const showComparison = !!selectedLocation && !comparisonCollapsed;
    const summaryGridHeight = showComparison ? summaryHeight : FULL_SUMMARY_HEIGHT;

    const cards = [
      { key: "total", label: "Total Locations", value: stats.total.toLocaleString(), Icon: LocationOnIcon, tone: Palette.total },
      { key: "balanced", label: "Balanced", value: stats.balanced.toLocaleString(), Icon: CheckCircleIcon, tone: Palette.balanced },
      { key: "differences", label: "Detail Differences", value: stats.differences.toLocaleString(), Icon: WarningIcon, tone: Palette.differences },
      { key: "missing", label: "Stock Missing", value: stats.missing.toLocaleString(), Icon: ErrorIcon, tone: Palette.missing },
      { key: "pallets", label: "Pallets on Files", value: stats.totalPallets.toLocaleString(), Icon: LayersIcon, tone: Palette.pallets },
    ];

    return (
      <div className={classes.root}>
        {/* Header */}
        <div className={classes.header}>
          <Button
            color="primary"
            variant="contained"
            onClick={this.loadSummary}
            disabled={loading}
            startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <RefreshIcon />}
          >
            {loading ? "Running Check..." : "Run Check"}
          </Button>
        </div>

        {/* Summary Cards */}
        <div className={classes.cardsContainer}>
          {cards.map(({ key, label, value, Icon, tone }) => (
            <Paper key={key} className={classes.card} elevation={0}>
              <div className={classes.iconBubble} style={{ backgroundColor: tone.bg }}>
                <Icon className={classes.cardIcon} style={{ color: tone.color }} />
              </div>
              <div className={classes.cardInfo}>
                <div className={classes.cardValue}>{value}</div>
                <div className={classes.cardLabel}>{label}</div>
              </div>
            </Paper>
          ))}
        </div>

        {/* Locations Grid */}
        <div className={classes.gridSection}>
          <div className={classes.gridHeader}>
            <span style={{ fontSize: 12, color: "#666" }}>Click a row to view comparison</span>
          </div>
          <Grid
            loading={loading}
            data={summaryData}
            forceHeight={summaryGridHeight}
            GridColumns={SummaryColumns}
            clearFilters={"stockchecksummary"}
            totalRowColumns={["Total Pallets", "Matched", "Mismatched", "Missing On System", "Missing On File"]}
            exportName={`${format(new Date(), "yyMMdd")}_STOCK_CHECK`}
            rowRenderer={(props) => this.summaryRowRenderer(props, classes)}
          />
        </div>

        {/* Detail Section - Side by Side Comparison */}
        <Collapse in={!!selectedLocation}>
          {selectedLocation && (
            <div>
              {/* drag to move space between the two grids, double click to reset */}
              {showComparison && (
                <div ref={this.handleRef} className={classes.resizeHandle} onMouseDown={this.startResize} onDoubleClick={this.resetResize} title="Drag to resize, double-click to reset">
                  <div className={classes.resizeGrip} />
                </div>
              )}

              <div className={classes.detailSection}>
                <div className={classes.detailHeader} style={comparisonCollapsed ? { marginBottom: 0 } : undefined}>
                  <div className={classes.detailTitleContainer}>
                    <span className={classes.locationBadge}>{selectedLocation["Location Code"]}</span>
                    <span className={classes.detailFile}>{selectedLocation.filename}</span>
                  </div>
                  <div>
                    <IconButton size="small" onClick={this.toggleComparison} title={comparisonCollapsed ? "Show comparison" : "Hide comparison"}>
                      {comparisonCollapsed ? <ExpandMoreIcon /> : <ExpandLessIcon />}
                    </IconButton>
                    <IconButton size="small" onClick={this.closeDetail}>
                      <CloseIcon />
                    </IconButton>
                  </div>
                </div>

                <Collapse in={!comparisonCollapsed} unmountOnExit>
                  {detailLoading ? (
                    <div className={classes.loadingOverlay}>
                      <CircularProgress />
                    </div>
                  ) : (
                    <div className={classes.splitterWrapper}>
                      <Splitter position="vertical" primaryPaneWidth="50%" dispatchResize={true} postPoned={true} className={classes.splitter}>
                        {/* Current System Data */}
                        <div className={classes.comparisonPane}>
                          <Grid
                            loading={false}
                            data={systemData}
                            forceHeight={comparisonHeight}
                            GridColumns={GridColumnsSystem}
                            clearFilters={"stockchecksystem"}
                            totalRowColumns={["No Cartons", "Pallet Size"]}
                            exportName={`${format(new Date(), "yyMMdd")}_STOCK_CHECK_${selectedLocation["Location Code"]}_SYSTEM`}
                            rowRenderer={(props) => this.comparisonRowRenderer(props, classes)}
                            toolbarChildren={this.renderPaneTitle("Current System Data", systemData.length, Palette.total)}
                          />
                        </div>

                        {/* PS File Data */}
                        <div className={classes.comparisonPane}>
                          <Grid
                            loading={false}
                            data={fileData}
                            forceHeight={comparisonHeight}
                            GridColumns={GridColumnsFile}
                            clearFilters={"stockcheckfile"}
                            totalRowColumns={["No Cartons", "Pallet Size"]}
                            exportName={`${format(new Date(), "yyMMdd")}_STOCK_CHECK_${selectedLocation["Location Code"]}_FILE`}
                            rowRenderer={(props) => this.comparisonRowRenderer(props, classes)}
                            toolbarChildren={this.renderPaneTitle("PS File Data", fileData.length, Palette.differences)}
                          />
                        </div>
                      </Splitter>
                    </div>
                  )}
                </Collapse>
              </div>
            </div>
          )}
        </Collapse>
      </div>
    );
  }
}

export default withStyles(styles)(StockCheckUnstyled);